import type { CSSProperties } from 'react';
import { locale, t } from '../i18n';
import { lowestListingPrice } from '../lib/listingPrice';
import { QT as T } from './theme';

/**
 * Annonskort i Q-chatten. Q visar plagg med verktyget show_listings (api: mcp/server.ts), och korten ritas
 * från verktygets svar i historiken, så att de finns kvar när chatten öppnas igen. Ett tryck öppnar annonsen
 * (händelsen qvestium:open-listing, som App.tsx lyssnar på).
 */

export interface ListingCard {
  id: string;
  name: string;
  imageUrl?: string | null;
  size?: string | null;
  description?: string | null;
  forSale: boolean;
  salePrice: number;
  forRent: boolean;
  rentPrice: number;
  rentPrice1?: number | null;
  rentPrice3?: number | null;
  rentPrice24?: number | null;
  forSwap?: boolean;
}

/** Verktygen vars svar blir kort: show_listings ger listings, create_listing_from_photo ett utkast. */
export const CARD_TOOLS = new Set(['show_listings', 'create_listing_from_photo']);

const resultText = (content: unknown): string =>
  typeof content === 'string' ? content
    : Array.isArray(content) ? content.map(c => (c && typeof c === 'object' && 'text' in c ? String((c as { text: unknown }).text) : '')).join('')
    : '';

const isCard = (x: unknown): x is ListingCard =>
  !!x && typeof x === 'object' && typeof (x as ListingCard).id === 'string' && typeof (x as ListingCard).name === 'string';

/** Korten i ett verktygssvar (tool_result.content). Tomt om svaret inte är kort, t.ex. ett fel eller avkortad text. */
export function listingCardsFrom(content: unknown): ListingCard[] {
  let data: unknown;
  try { data = JSON.parse(resultText(content)); } catch { return []; }
  const list = isCard(data) ? [data] : Array.isArray((data as { listings?: unknown })?.listings) ? (data as { listings: unknown[] }).listings : [];
  return list.filter(isCard).slice(0, 6);
}

/**
 * Svaret från ett verktyg som ChatGPT-rösten körde (POST /api/q/realtime/tool, api: q/realtime.ts): output är
 * { status, result } där result är verktygets text. Ger texten, så att samma kort kan ritas som i textchatten.
 */
export function realtimeToolContent(output: string | undefined): string | null {
  if (!output) return null;
  try {
    const o = JSON.parse(output) as { status?: string; result?: unknown };
    return o?.status === 'done' && typeof o.result === 'string' ? o.result : null;
  } catch {
    return null;
  }
}

/** Bilder som API:t sparat relativt ("/uploads/images/…") ligger hos API:t, inte på qvestium.com. */
export function cardImageUrl(url: string | null | undefined, apiBase: (path: string) => string): string | null {
  if (!url) return null;
  return url.startsWith('/uploads/') ? apiBase(url) : url;
}

/** "Från 450kr" (lägsta av köp och en månads hyra) eller "Byte", som annonskorten i marknaden. */
export function cardPriceText(card: ListingCard): string {
  const lowest = lowestListingPrice({
    forSale: card.forSale, salePrice: Number(card.salePrice ?? 0),
    forRent: card.forRent, rentPrice: Number(card.rentPrice ?? 0), rentPrice1: card.rentPrice1 ?? null,
  });
  if (lowest == null) return card.forSwap ? t('marketplaceWardrobeCard.swapOnly') : t('qCards.draft');
  return t('common.fromKr', { amount: Math.round(lowest).toLocaleString(locale()) });
}

/** Märket ur importerade plaggs beskrivning ("Märke · storlek · skick"), som cardBrand i App.tsx. */
export function cardBrand(card: Pick<ListingCard, 'description' | 'size'>): string | null {
  const parts = card.description?.trim().split(' · ').map(p => p.trim()) ?? [];
  if (parts.length >= 2 && card.size && parts.slice(1).includes(card.size.trim())) return parts[0] || null;
  return null;
}

export function openListing(id: string) {
  window.dispatchEvent(new CustomEvent('qvestium:open-listing', { detail: id }));
}

/** Händelsen som får Min garderob att läsa om plaggen (App.tsx). */
export const WARDROBE_CHANGED = 'qvestium:wardrobe-changed';

/** Verktyg som ändrar garderoben: när något av dem körts läser Min garderob om. */
const WARDROBE_TOOLS = new Set(['create_listing_from_photo', 'create_listing', 'update_listing', 'delete_listing', 'create_drop', 'cancel_drop']);

export function changesWardrobe(toolName: string): boolean {
  return WARDROBE_TOOLS.has(toolName);
}

export function notifyWardrobeChanged() {
  window.dispatchEvent(new Event(WARDROBE_CHANGED));
}

// ---------- Bilder som personen skickar ----------
// En bifogad bild går till Claude som bild, och originalet laddas också upp (POST /api/upload). Adressen följer
// med som en textrad, så att Q kan skapa ett annonsutkast av bilden (create_listing_from_photo). Raden visas inte
// i chatten.

const UPLOAD_NOTE = /^\[Uppladdad bild: (\/uploads\/images\/[\w.-]+)\]$/;

/** Textraden för en uppladdad bild. Adressen görs relativ ("/uploads/images/…"), som servern sparar den. */
export function uploadNote(url: string): string {
  return `[Uppladdad bild: ${url.replace(/^https?:\/\/[^/]+(?=\/uploads\/)/, '')}]`;
}

export function isUploadNote(text: string): boolean {
  return UPLOAD_NOTE.test(text.trim());
}

const line: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/** Korten i en rad som går att svepa i sidled; ett kort ensamt blir lite bredare. */
export function QListingCards({ cards, onOpen, apiBase = p => p }: { cards: ListingCard[]; onOpen: (id: string) => void; apiBase?: (path: string) => string }) {
  if (!cards.length) return null;
  const width = cards.length === 1 ? 168 : 140;
  return (
    <div data-q-cards style={{ display: 'flex', gap: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%', padding: '2px 0 4px', scrollSnapType: 'x mandatory' }}>
      {cards.map(card => {
        const brand = cardBrand(card);
        return (
          <a
            key={card.id}
            href={`/?item=${encodeURIComponent(card.id)}`}
            data-q-card={card.id}
            onClick={e => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // ny flik som vanligt
              e.preventDefault();
              onOpen(card.id);
            }}
            style={{ flex: `0 0 ${width}px`, width, minWidth: 0, color: 'inherit', textDecoration: 'none', scrollSnapAlign: 'start', borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden', background: T.surface }}
          >
            <div style={{ position: 'relative', paddingTop: '125%', background: '#ffffff' }}>
              {card.imageUrl
                ? <img src={cardImageUrl(card.imageUrl, apiBase)!} alt={card.name} loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div aria-hidden style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 32, color: T.textMuted }}>👗</div>}
            </div>
            <div style={{ padding: '8px 9px 10px' }}>
              <div style={{ ...line, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: T.text }}>{brand ?? card.name}</div>
              {(brand || card.size) && <div style={{ ...line, fontSize: 12, color: T.textSub, marginTop: 1 }}>{[brand && card.name, card.size && t('marketplaceWardrobeCard.sizeShort', { size: card.size })].filter(Boolean).join(' · ')}</div>}
              <div style={{ ...line, fontSize: 13, fontWeight: 700, color: T.text, marginTop: 4 }}>{cardPriceText(card)}</div>
            </div>
          </a>
        );
      })}
    </div>
  );
}
